/**
 * PipelineBoard — the Intake Pipeline drag-and-drop kanban, matched to the
 * prototype (PR4-fidelity): a Needs-you lane above; columns with parent phase
 * grouping (phase_label + owner chip), an accent dot, a count chip; and rich
 * cards. Receives rows + stages as props (Workspace owns the fetch so the
 * header counts share the cache). Cross-lane DnD + the pure resolveStageMove()
 * are unchanged from PR4a; the server still enforces 409 gate_not_met.
 *
 * Founder soak item 3: WITHIN a lane, cards also sort — a per-stage sort
 * control (newest / due date / my order) plus drag-to-reorder (which flips
 * the lane to "my order"). Both are per-user localStorage view preferences
 * (src/lib/laneSort.ts), not shared team state.
 */

import { useCallback, useMemo, useRef, useState, useEffect, type CSSProperties, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canManageWorkItems } from '@/lib/capabilities';
import { useMoveStage, useWorkspaceMembers, usePatchWorkItem } from '@/lib/workspace';
import { resolveStageMove, ASSIGNED_STAGE_KEY, splitProspectName } from '@/lib/workspaceStageMove';
import {
  applyManualReorder,
  laneSortStorageKey,
  loadLaneSortPrefs,
  saveLaneSortPrefs,
  sortLane,
  type LaneSortMode,
  type LaneSortPrefs,
} from '@/lib/laneSort';
import {
  laneCollapseStorageKey,
  loadCollapsedLanes,
  saveCollapsedLanes,
  toggleCollapsedLane,
} from '@/lib/laneCollapse';
import type { WorkItemRow, MoveStageInput, PipelineStageWire } from '@/lib/api';
import type { ProcessCardFolderLink } from '@/lib/processCardFields';
import { WorkItemCard } from './WorkItemCard';
import { HandoffDialog } from './HandoffDialog';
import { MoveStageDialog, type MoveStageMode } from './MoveStageDialog';
import { NeedsYouLane, type NeedsYouItem } from './NeedsYouLane';
import { cardName, isOverdue, shortDate, moveBlockedMessage } from './workspaceFormat';
import { useSupportContact } from '@/hooks/useSupportContact';
import { SelectField } from '@/components/primitives/SelectField';

interface PendingMove {
  id: string;
  toStage: string;
  mode: MoveStageMode;
  firstName: string;
  lastName: string;
  /** The intake card's ZIP and client, for the Assign dialog's suggestions. */
  prospectZip?: string | null;
  clientId?: string | null;
}

/** Tenant-neutral accent: early stages sage, last two gold. */
function accentClass(i: number, n: number): string {
  if (i >= n - 2) return 'bg-warm-400';
  if (i >= Math.ceil(n / 2)) return 'bg-sage-800';
  if (i === 0) return 'bg-sage-300';
  return 'bg-sage-400';
}

function humanizeOwner(role: string | null): string | null {
  if (!role) return null;
  // Founder soak: the Client Setup stage owner reads "Client services".
  const map: Record<string, string> = { data_steward: 'Client services', exec_support: 'Bookkeeping' };
  return map[role] ?? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function SortableCard({ item, onOpen, onAssign, canDrag, dueDateLabel, stageKey, folderLink }: { item: WorkItemRow; onOpen: (id: string) => void; onAssign?: (item: WorkItemRow) => void; canDrag: boolean; dueDateLabel?: string | null; stageKey?: string | null; folderLink?: ProcessCardFolderLink | null }): ReactNode {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canDrag });
  const style: CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="mb-2.5 flex items-stretch gap-1">
      {canDrag && (
        <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners}
          aria-label={`Drag ${item.display_name ?? cardName(item)} to another stage`} data-testid="card-drag-handle"
          className="flex-shrink-0 px-0.5 flex items-center text-sage-300 hover:text-sage-600 cursor-grab touch-none rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
          <GripVertical size={16} aria-hidden="true" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <WorkItemCard item={item} onOpen={onOpen} onAssign={onAssign} dueDateLabel={dueDateLabel} stageKey={stageKey} folderLink={folderLink} />
      </div>
    </div>
  );
}

function Column({
  stage, index, total, items, onOpen, onAssign, canDrag, sortMode, onSortModeChange, categoryField, dueDateLabel, folderLink, collapsed, onToggleCollapse,
}: {
  stage: PipelineStageWire; index: number; total: number; items: WorkItemRow[]; onOpen: (id: string) => void; onAssign?: (item: WorkItemRow) => void; canDrag: boolean;
  /** Founder soak item 3: this lane's sort mode + its setter (per-user pref). */
  sortMode: LaneSortMode; onSortModeChange: (mode: LaneSortMode) => void;
  /** Field pack v1: the board's configured category field ({label}), or null.
   *  Non-null adds the config-labeled category sort option to the lane. */
  categoryField?: { label: string } | null;
  /** Field pack v1: the board's configured due-date label (card tooltip). */
  dueDateLabel?: string | null;
  /** The board's configured folder link, handed to every card. */
  folderLink?: ProcessCardFolderLink | null;
  /** Founder soak: this group's collapsed state + its toggle (per-user pref,
   *  src/lib/laneCollapse.ts). Collapsed hides the cards; the header count
   *  chip stays, and the column remains a live drop target. */
  collapsed: boolean; onToggleCollapse: () => void;
}): ReactNode {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${stage.key}` });
  const who = humanizeOwner(stage.owner_role);
  return (
    // fix #5: wider columns (w-72) + a little more inner padding so cards breathe.
    <div ref={setNodeRef} data-testid={`pipeline-column-${stage.key}`} className={'flex-shrink-0 w-72 rounded-2xl p-3.5 ' + (isOver ? 'bg-sage-100' : 'bg-sand')}>
      <div className="h-4 mx-1.5 mb-1 flex items-center gap-2">
        {stage.phase_label && (
          <>
            <span className="text-[9.5px] font-bold uppercase tracking-wider text-sage-600">{stage.phase_label}</span>
            {who && <span className="text-[9.5px] font-bold text-warm-700 bg-warm-100 rounded-pill px-1.5 py-px">{who}</span>}
          </>
        )}
      </div>
      <h3 className="text-[13px] font-bold text-sage-800 flex items-center gap-2 mx-1.5 mb-1.5">
        {/* Collapse affordance (founder soak: a long group like "Did not
            extend role" must fold away). A quiet chevron button; the count
            chip on the right keeps the group's size visible while folded. */}
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${stage.label}` : `Collapse ${stage.label}`}
          data-testid={`lane-collapse-${stage.key}`}
          onClick={onToggleCollapse}
          className="flex-shrink-0 -ml-1 p-0.5 rounded text-sage-400 hover:text-sage-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {collapsed ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
        </button>
        <span className={'w-[7px] h-[7px] rounded-pill flex-shrink-0 ' + accentClass(index, total)} aria-hidden="true" />
        {stage.label}
        <span className="ml-auto bg-warm-100 text-sage-600 rounded-pill px-2 py-0.5 text-[11px] font-bold">{items.length}</span>
      </h3>
      {/* Collapsed: cards + the sort control fold away; header (with count)
          stays, and dropping onto the column still lands at the lane's end. */}
      {!collapsed && (
        <>
          {/* Item 3b: the per-stage sort control. Dragging a card within the lane
              flips the lane to "My order" automatically (see onDragEnd). */}
          <div className="mx-1.5 mb-2.5 flex items-center">
            <SelectField size="compact" quiet
              value={sortMode}
              onChange={(e) => onSortModeChange(e.target.value as LaneSortMode)}
              aria-label={`Sort ${stage.label}`}
              data-testid={`lane-sort-${stage.key}`}
            >
              <option value="created">Sort: newest</option>
              <option value="due">Sort: due date</option>
              <option value="next_action">Sort: next action</option>
              {/* Founder soak (talent board): alphabetical by the card's name. */}
              <option value="name">Sort: name</option>
              <option value="manual">Sort: my order</option>
              {/* Field pack v1: sort by the board's configured field — the option
                  label comes from config ("Sort: role"), never a hardcoded name. */}
              {categoryField && <option value="category">Sort: {categoryField.label.toLowerCase()}</option>}
            </SelectField>
          </div>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {/* Founder soak: the column's stage key rides along so the card can
                render its Case Assignment extras (folder link + CM row). */}
            {items.map((item) => (
              <SortableCard key={item.id} item={item} onOpen={onOpen} onAssign={onAssign} canDrag={canDrag} dueDateLabel={dueDateLabel} stageKey={stage.key} folderLink={folderLink} />
            ))}
          </SortableContext>
        </>
      )}
    </div>
  );
}

export interface PipelineBoardProps {
  rows: WorkItemRow[];
  stages: PipelineStageWire[];
  onOpenItem: (id: string) => void;
  /** Field pack v1: the board's configured category field ({label}), or
   *  null/absent — gates the per-lane category sort option. */
  categoryField?: { label: string } | null;
  /** Field pack v1: the board's configured due-date label (e.g. a hiring
   *  board's "Application date"); threads to the cards. */
  dueDateLabel?: string | null;
  /** The board's configured folder link (a talent board's candidate
   *  folder); threads to every card. */
  folderLink?: ProcessCardFolderLink | null;
  /** The process's immutable key ('intake' for the system board) — scopes the
   *  per-user collapsed-groups storage so two boards sharing a stage key
   *  (both could have an "applied") never fold together. */
  boardKey?: string;
}

/** Thumb metrics from the target's scroll geometry. ratio = visible/total (the
 *  thumb's width fraction); posFrac = the thumb's left fraction of the track
 *  (0..1-ratio), tracking scrollLeft. Pure — unit-tested (jsdom has no layout). */
export function scrollbarMetrics(scrollLeft: number, scrollWidth: number, clientWidth: number): { ratio: number; posFrac: number } {
  const ratio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
  const maxScroll = scrollWidth - clientWidth;
  return { ratio, posFrac: maxScroll > 0 ? (scrollLeft / maxScroll) * (1 - ratio) : 0 };
}

/** The board scrollLeft for a thumb dragged `dx` px on a `trackWidth`-wide track
 *  (which spans the full content width). The pure inverse of scrollbarMetrics. */
export function scrollLeftFromDrag(startScroll: number, dx: number, trackWidth: number, scrollWidth: number): number {
  return trackWidth > 0 ? startScroll + (dx / trackWidth) * scrollWidth : startScroll;
}

/**
 * A slim custom horizontal scrollbar mounted ABOVE the board columns; the native
 * bar is hidden (scrollbar-none). A faint hairline track + a soft sage thumb sized
 * to the visible/total ratio, synced both ways to the target's scrollLeft (scroll
 * the board → the thumb moves; drag the thumb → the board scrolls). Keyboard:
 * focusable, arrows nudge. Renders nothing when there's no horizontal overflow.
 */
function PipelineScrollbar({ targetRef }: { targetRef: React.RefObject<HTMLDivElement | null> }): ReactNode {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startScroll: number } | null>(null);
  const [m, setM] = useState({ ratio: 1, posFrac: 0 }); // ratio = client/scroll; posFrac = thumb-left fraction of the track

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const update = (): void => setM(scrollbarMetrics(el.scrollLeft, el.scrollWidth, el.clientWidth));
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [targetRef]);

  if (m.ratio >= 1) return null; // no overflow → no control

  function onPointerDown(e: React.PointerEvent): void {
    const el = targetRef.current;
    if (!el) return;
    dragRef.current = { startX: e.clientX, startScroll: el.scrollLeft };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent): void {
    const el = targetRef.current;
    const track = trackRef.current;
    if (!dragRef.current || !el || !track) return;
    // The track spans the full content width, so dx px of thumb travel maps to
    // dx/trackWidth * scrollWidth px of content (exact, accounting for thumb size).
    el.scrollLeft = scrollLeftFromDrag(dragRef.current.startScroll, e.clientX - dragRef.current.startX, track.clientWidth, el.scrollWidth);
  }
  function onPointerUp(e: React.PointerEvent): void {
    dragRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  }
  function nudge(dir: number): void {
    const el = targetRef.current;
    if (el) el.scrollLeft += dir * Math.max(120, el.clientWidth * 0.3);
  }

  return (
    <div ref={trackRef} data-testid="pipeline-scrollbar" className="relative mb-2.5 h-[3px] rounded-pill bg-sage-200/40">
      <div
        role="scrollbar"
        aria-orientation="horizontal"
        aria-label="Scroll the pipeline"
        aria-controls="pipeline-board"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round((m.posFrac / Math.max(0.0001, 1 - m.ratio)) * 100)}
        tabIndex={0}
        data-testid="pipeline-scrollbar-thumb"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
        }}
        style={{ width: `${m.ratio * 100}%`, left: `${m.posFrac * 100}%` }}
        className="absolute top-1/2 h-[5px] -translate-y-1/2 cursor-grab rounded-pill bg-sage-400 transition-[height] hover:h-[7px] active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      />
    </div>
  );
}

export function PipelineBoard({ rows, stages, onOpenItem, categoryField = null, dueDateLabel = null, folderLink = null, boardKey = 'intake' }: PipelineBoardProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const contact = useSupportContact();
  const canManage = canManageWorkItems(whoami);
  const moveStage = useMoveStage(tenantSlug);
  // Polish fix #2: assign/reassign the process owner from a card's avatar.
  const patchItem = usePatchWorkItem(tenantSlug);
  const { members } = useWorkspaceMembers(tenantSlug, { enabled: canManage });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const [assigning, setAssigning] = useState<WorkItemRow | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const stageKeyById = useMemo(() => new Map(stages.map((s) => [s.id, s.key])), [stages]);
  const stageLabelById = useMemo(() => new Map(stages.map((s) => [s.id, s.label])), [stages]);
  const cardsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  // Founder soak item 3: per-user, per-stage lane ordering (sort mode + manual
  // order), persisted in localStorage (a VIEW preference, not shared team
  // state — see src/lib/laneSort.ts). whoami resolves async, so the prefs
  // reload when the storage key settles.
  const laneKey = laneSortStorageKey(tenantSlug ?? '', whoami?.user_id ?? '');
  const [lanePrefs, setLanePrefs] = useState<LaneSortPrefs>(() => loadLaneSortPrefs(localStorage, laneKey));
  useEffect(() => {
    setLanePrefs(loadLaneSortPrefs(localStorage, laneKey));
  }, [laneKey]);
  const updateLanePrefs = useCallback((updater: (prev: LaneSortPrefs) => LaneSortPrefs): void => {
    setLanePrefs((prev) => {
      const next = updater(prev);
      saveLaneSortPrefs(localStorage, laneKey, next);
      return next;
    });
  }, [laneKey]);
  const laneMode = useCallback(
    (stageKey: string): LaneSortMode => lanePrefs.modes[stageKey] ?? 'created',
    [lanePrefs],
  );

  // Founder soak: collapsed groups — per-user, per-BOARD (stage keys are only
  // unique within one board), persisted like the lane sort. whoami resolves
  // async, so the state reloads when the storage key settles.
  const collapseKey = laneCollapseStorageKey(tenantSlug ?? '', whoami?.user_id ?? '', boardKey);
  const [collapsedLanes, setCollapsedLanes] = useState<ReadonlySet<string>>(() => loadCollapsedLanes(localStorage, collapseKey));
  useEffect(() => {
    setCollapsedLanes(loadCollapsedLanes(localStorage, collapseKey));
  }, [collapseKey]);
  const toggleLane = useCallback((stageKey: string): void => {
    setCollapsedLanes((prev) => {
      const next = toggleCollapsedLane(prev, stageKey);
      saveCollapsedLanes(localStorage, collapseKey, next);
      return next;
    });
  }, [collapseKey]);

  const byStage = useMemo(() => {
    const m = new Map<string, WorkItemRow[]>();
    for (const s of stages) m.set(s.key, []);
    for (const r of rows) {
      const key = r.stage_id ? stageKeyById.get(r.stage_id) : undefined;
      if (key && m.has(key)) m.get(key)!.push(r);
    }
    // Apply each lane's sort (per-user pref); 'created' mirrors the server order.
    for (const [key, items] of m) {
      m.set(key, sortLane(items, laneMode(key), lanePrefs.manual[key]));
    }
    return m;
  }, [rows, stages, stageKeyById, laneMode, lanePrefs]);

  const needsYou = useMemo<NeedsYouItem[]>(() => {
    const out: NeedsYouItem[] = [];
    for (const r of rows) {
      // PR3: the Needs-You lane is intake-only — its CTAs (send paperwork,
      // assign a CM) are intake semantics; process_item cards never enter.
      if (r.kind !== 'intake') continue;
      const sk = r.stage_id ? stageKeyById.get(r.stage_id) : null;
      const stageLabel = r.stage_id ? stageLabelById.get(r.stage_id) : null;
      // Team fix 2026-07-16: prefer the server-canonical display_name (mirrors
      // WorkItemCard). An intake rename PATCHes prospect_name, never title, so
      // bare cardName() kept showing the creation-time title here.
      const name = r.display_name ?? cardName(r);
      if (isOverdue(r)) out.push({ id: r.id, name, note: `${stageLabel ? stageLabel + ' · ' : ''}overdue ${shortDate(r.due_date)}`, cta: 'Send', action: 'open' });
      else if (sk === 'ready_for_assignment' && !r.assignee_roster_id) out.push({ id: r.id, name, note: 'Ready for assignment · no CM yet', cta: 'Assign care manager', action: 'assign' });
    }
    return out;
  }, [rows, stageKeyById, stageLabelById]);

  const sendN = needsYou.filter((n) => n.action !== 'assign').length;
  const assignN = needsYou.filter((n) => n.action === 'assign').length;
  const needsSummary = [sendN ? `${sendN} paperwork overdue` : '', assignN ? `${assignN} awaiting CM` : ''].filter(Boolean).join(', ');

  function runMove(id: string, input: MoveStageInput): void {
    setMoveError(null);
    moveStage.mutate({ id, input }, {
      onError: (err) => setMoveError(moveBlockedMessage(err, contact)), // names the unfinished item(s); never "gate"
    });
  }

  function onDragStart(e: DragStartEvent): void { setActiveId(String(e.active.id)); }
  function onDragEnd(e: DragEndEvent): void {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const card = cardsById.get(String(active.id));
    if (!card) return;
    const overId = String(over.id);
    const targetStageKey = overId.startsWith('col:') ? overId.slice(4) : stageKeyById.get(cardsById.get(overId)?.stage_id ?? '');
    if (!targetStageKey) return;
    const currentStageKey = card.stage_id ? (stageKeyById.get(card.stage_id) ?? null) : null;
    // Item 3a: a drop WITHIN the card's own lane is a manual reorder, not a
    // stage move (resolveStageMove's same-stage guard would no-op it). Reorder
    // against the lane AS DISPLAYED, persist it, and flip the lane to 'manual'
    // so the drag visibly sticks whatever mode was active. Dropping onto the
    // column itself (not a card) sends the card to the end of the lane.
    if (targetStageKey === currentStageKey && currentStageKey !== null) {
      const displayed = (byStage.get(currentStageKey) ?? []).map((i) => i.id);
      const reordered = overId.startsWith('col:')
        ? (displayed.includes(card.id) ? [...displayed.filter((x) => x !== card.id), card.id] : null)
        : applyManualReorder(displayed, card.id, overId);
      if (reordered) {
        updateLanePrefs((prev) => ({
          modes: { ...prev.modes, [currentStageKey]: 'manual' },
          manual: { ...prev.manual, [currentStageKey]: reordered },
        }));
      }
      return;
    }
    // PR3: pass the card's kind — a process_item drop is ALWAYS a plain move
    // (never the intake Client Setup / Assign dialogs, even on a same-key stage).
    const decision = resolveStageMove(targetStageKey, { currentStageKey, hasClient: card.client_id !== null, kind: card.kind });
    if (!decision) return;
    if (decision.action === 'move') runMove(card.id, { to_stage: decision.toStage });
    else if (decision.action === 'open-client-setup') {
      const { first, last } = splitProspectName(card.prospect_name);
      setPending({ id: card.id, toStage: decision.toStage, mode: 'client-setup', firstName: first, lastName: last, prospectZip: card.prospect_zip });
    } else {
      setPending({
        id: card.id,
        toStage: decision.toStage,
        mode: 'assign',
        firstName: '',
        lastName: '',
        prospectZip: card.prospect_zip,
        clientId: card.client_id,
      });
    }
  }

  const activeCard = activeId ? cardsById.get(activeId) : null;

  return (
    <div>
      {/* The Needs-You lane is an ACTION strip (Send paperwork / Assign a CM),
          so it renders only for a caller who can act on the pipeline
          (canManageWorkItems). A read-only viewer (board_observer, 0156 founder
          screenshot) sees the board columns but never this banner — the server
          re-checks every action gate regardless. */}
      {canManage && (
        <NeedsYouLane
          items={needsYou}
          summary={needsSummary}
          onOpen={onOpenItem}
          onAssign={(id) =>
            setPending({
              id,
              toStage: ASSIGNED_STAGE_KEY,
              mode: 'assign',
              firstName: '',
              lastName: '',
              prospectZip: rows.find((r) => r.id === id)?.prospect_zip ?? null,
              clientId: rows.find((r) => r.id === id)?.client_id ?? null,
            })
          }
        />
      )}
      <div aria-live="polite" className="sr-only" data-testid="board-live">{moveError ?? ''}</div>
      {moveError && <p role="alert" data-testid="board-move-error" className="text-sm text-pepper px-1 py-2">{moveError}</p>}

      <PipelineScrollbar targetRef={boardRef} />
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div ref={boardRef} id="pipeline-board" className="flex gap-4 items-start overflow-x-auto scrollbar-none" data-testid="pipeline-board">
          {stages.map((s, i) => (
            <Column
              key={s.key}
              stage={s}
              index={i}
              total={stages.length}
              items={byStage.get(s.key) ?? []}
              onOpen={onOpenItem}
              onAssign={canManage ? setAssigning : undefined}
              canDrag={canManage}
              sortMode={laneMode(s.key)}
              onSortModeChange={(mode) => updateLanePrefs((prev) => ({ ...prev, modes: { ...prev.modes, [s.key]: mode } }))}
              categoryField={categoryField}
              dueDateLabel={dueDateLabel}
              folderLink={folderLink}
              collapsed={collapsedLanes.has(s.key)}
              onToggleCollapse={() => toggleLane(s.key)}
            />
          ))}
        </div>
        <DragOverlay>{activeCard ? <WorkItemCard item={activeCard} onOpen={() => {}} /> : null}</DragOverlay>
      </DndContext>

      {pending && (
        <MoveStageDialog
          mode={pending.mode}
          toStage={pending.toStage}
          defaultFirstName={pending.firstName}
          defaultLastName={pending.lastName}
          prospectZip={pending.prospectZip ?? null}
          workItemId={pending.id}
          clientId={pending.clientId ?? null}
          submitting={moveStage.isPending}
          onCancel={() => setPending(null)}
          onConfirm={(payload) => { runMove(pending.id, { to_stage: pending.toStage, ...payload }); setPending(null); }}
        />
      )}

      {/* Polish fix #2: assign/reassign the process owner from the card avatar.
          Reuses the handoff picker (assignee_user_id + its bell) — NEVER the CM. */}
      {assigning && (
        <HandoffDialog
          members={members}
          currentUserId={assigning.assignee_user_id}
          currentUserName={assigning.assignee_name}
          submitting={patchItem.isPending}
          onHandoff={(uid) => patchItem.mutate({ id: assigning.id, edits: { assignee_user_id: uid } }, { onSuccess: () => setAssigning(null) })}
          onSkip={() => setAssigning(null)}
        />
      )}
    </div>
  );
}
